import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API_BASE } from "@/lib/api";
import { STATUS_LABEL, STATUS_BADGE, PAYMENT_BADGE } from "@/lib/statusLabels";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Download, Search } from "lucide-react";
import { toast } from "sonner";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders/mine").then((r) => { setOrders(r.data.orders); setLoading(false); });
  }, []);

  const filtered = orders.filter((o) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (o.order_number || "").toLowerCase().includes(s) || (o.service_name || "").toLowerCase().includes(s);
  });

  const downloadInvoice = async (o) => {
    try {
      const token = localStorage.getItem("w2e_token");
      const res = await fetch(`${API_BASE}/orders/${o.id}/invoice`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${o.order_number || "invoice"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download invoice");
    }
  };

  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="section-label">My Filings</div>
        <h1 className="mt-1 font-display font-bold text-3xl text-[#0A192F]">All Orders</h1>
      </div>

      <div className="mb-4 max-w-sm relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Search by order # or service" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="orders-search" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 text-slate-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <div>No orders match your search.</div>
            <Link to="/services" className="text-[#1E3A8A] mt-2 inline-block">Browse services →</Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Order #</th>
                  <th className="text-left px-4 py-3">Service</th>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Payment</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((o) => (
                  <tr key={o.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono font-semibold text-[#0A192F]">{o.order_number || "—"}</td>
                    <td className="px-4 py-3">{o.service_name}</td>
                    <td className="px-4 py-3 text-slate-600">{new Date(o.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3"><span className={`status-badge ${PAYMENT_BADGE[o.payment_status] || "badge-muted"}`}>{o.payment_status}</span></td>
                    <td className="px-4 py-3"><span className={`status-badge ${STATUS_BADGE[o.current_status] || "badge-muted"}`}>{STATUS_LABEL[o.current_status]}</span></td>
                    <td className="px-4 py-3 text-right font-medium">₹{o.amount}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {o.payment_status === "paid" && (
                        <Button variant="outline" size="sm" onClick={() => downloadInvoice(o)} className="mr-2" data-testid={`invoice-dl-${o.id}`}>
                          <Download className="w-3.5 h-3.5 mr-1" /> Invoice
                        </Button>
                      )}
                      <Link to={`/dashboard/orders/${o.id}`}><Button size="sm" className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid={`view-order-${o.id}`}>View</Button></Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
